import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Col,
  Layout,
  Pagination,
  RadioChangeEvent,
  Row,
  Typography,
  Divider,
  Button,
  Drawer,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { BASE_URL } from "../../constants/urlConstants";

import MenuCheckboxSection from "./MenuSection/MenuCheckboxSection";
import MenuRadioSection from "./MenuSection/MenuRadioSection";
import PerfumeCard from "../../components/PerfumeCard/PerfumeCard";
import Spinner from "../../components/Spinner/Spinner";
import { MAX_PAGE_VALUE, usePagination } from "../../hooks/usePagination";
import { useSearch } from "../../hooks/useSearch";
import { FilterParamsType } from "../../types/types";
import { fetchPerfumesByFilterParams } from "../../redux-toolkit/perfumes/perfumes-thunks";
import {
  selectIsPerfumesLoading,
  selectPerfumes,
} from "../../redux-toolkit/perfumes/perfumes-selector";
import "./Menu.css";

export enum CheckboxCategoryFilter {
  BRANDS = "BRANDS",
  CATEGORIES = "CATEGORIES",
  SUBCATEGORIES = "SUBCATEGORIES",
  GENDERS = "GENDERS",
}

type LocationStateType = {
  perfumes?: any[];
  searchText?: string;
};

const Menu: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const perfumes = useSelector(selectPerfumes) || [];
  const isPerfumesLoading = useSelector(selectIsPerfumesLoading);
  const location = useLocation<LocationStateType>();

  const [brands, setBrands] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>(["Male", "Female", "Unisex"]);

  const [noProducts, setNoProducts] = useState(false);
  const [skipReduxLoading, setSkipReduxLoading] = useState(false);

  const [filterParams, setFilterParams] = useState<FilterParamsType>({
    brands: [],
    categories: [],
    subCategories: [],
    genders: [],
    prices: [1, 9999],
  });

  const [sortByPrice, setSortByPrice] = useState<boolean>(false);
  const { currentPage, totalElements, handleChangePagination } = usePagination();
  const { resetFields } = useSearch();

  const [localPerfumes, setLocalPerfumes] = useState<any[]>([]);
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  // Mobile UI
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch filters
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/v1/perfumes/filters`);
        const { brands, categories, subCategories } = res.data;
        setBrands(brands || []);
        setCategories(categories || []);
        setSubCategories(subCategories || []);
      } catch (err) {
        console.error("Failed to fetch filters:", err);
      }
    };
    fetchFilters();
  }, []);

  // MAIN LOADING LOGIC
  useEffect(() => {
    const fetchData = async () => {
      const queryParams = new URLSearchParams(location.search);
      const brandParam = queryParams.get("brand");
      const searchText = queryParams.get("search") || "";

      setLocalPerfumes([]);
      setNoProducts(false);
      setSkipReduxLoading(false);

      // Brand search
      if (brandParam) {
        setIsLocalLoading(true);
        try {
          const res = await axios.post(`${BASE_URL}/api/v1/perfumes/search?page=0`, {
            brands: [brandParam],
            categories: [],
            subCategories: [],
            genders: [],
            prices: [1, 9999],
            sortByPrice: false,
            currentPage: 0,
          });

          setLocalPerfumes(res.data || []);
        } finally {
          setIsLocalLoading(false);
        }
        return;
      }

      // Global navbar search
      if (location.state?.perfumes) {
        setLocalPerfumes(location.state.perfumes);
        return;
      }

      // CATEGORY SEARCH (text search)
      if (searchText) {
        setIsLocalLoading(true);
        setSkipReduxLoading(true);

        try {
          const res = await axios.post(
            `${BASE_URL}/api/v1/perfumes/search/text?page=0`,
            {
              searchType: "CATEGORY",
              text: searchText,
              currentPage: 0,
            }
          );

          const perfumesData = res.data || [];

          if (perfumesData.length === 0) {
            setLocalPerfumes([]);
            setNoProducts(true);
            return;
          }

          setLocalPerfumes(perfumesData);
        } finally {
          setIsLocalLoading(false);
        }

        return;
      }

      // Default backend load
      setIsLocalLoading(true);
      try {
        await dispatch(
          fetchPerfumesByFilterParams({
            ...filterParams,
            sortByPrice,
            currentPage: 0,
          })
        );
      } finally {
        setIsLocalLoading(false);
      }
    };

    fetchData();
  }, [location.search, location.state]);

  // Checkbox filter updates
  const onChangeCheckbox = (
    checkedValues: CheckboxValueType[],
    category: CheckboxCategoryFilter
  ) => {
    const newFilter = { ...filterParams };

    if (category === CheckboxCategoryFilter.BRANDS)
      newFilter.brands = checkedValues as string[];
    if (category === CheckboxCategoryFilter.CATEGORIES)
      newFilter.categories = checkedValues as string[];
    if (category === CheckboxCategoryFilter.SUBCATEGORIES)
      newFilter.subCategories = checkedValues as string[];
    if (category === CheckboxCategoryFilter.GENDERS)
      newFilter.genders = checkedValues as string[];

    setFilterParams(newFilter);
    dispatch(
      fetchPerfumesByFilterParams({
        ...newFilter,
        sortByPrice,
        currentPage: 0,
      })
    );
    resetFields();
  };

  // Radio filter
  const onChangeRadio = (event: RadioChangeEvent) => {
    const priceRange = event.target.value?.array || [1, 9999];
    const updated = { ...filterParams, prices: priceRange };
    setFilterParams(updated);

    dispatch(
      fetchPerfumesByFilterParams({
        ...updated,
        sortByPrice,
        currentPage: 0,
      })
    );
  };

  // Sort mobile
  const handleMobileSort = (direction: "asc" | "desc") => {
    const sortFlag = direction === "desc";
    setSortByPrice(sortFlag);

    if (localPerfumes.length > 0) {
      const sorted = [...localPerfumes].sort((a, b) =>
        sortFlag ? b.price - a.price : a.price - b.price
      );
      setLocalPerfumes(sorted);
    } else {
      dispatch(
        fetchPerfumesByFilterParams({
          ...filterParams,
          sortByPrice: sortFlag,
          currentPage: 0,
        })
      );
    }
  };

  // Pagination
  const changePagination = (page: number, pageSize: number) => {
    dispatch(
      fetchPerfumesByFilterParams({
        ...filterParams,
        sortByPrice,
        currentPage: page - 1,
      })
    );
    handleChangePagination(page, pageSize);
  };

  const FilterContent = (
    <>
      <Typography.Title level={4}>Filters</Typography.Title>
      <Divider />
      <MenuCheckboxSection
        title="Brands"
        onChange={onChangeCheckbox}
        data={brands}
        category={CheckboxCategoryFilter.BRANDS}
        selectedValues={filterParams.brands || []}
      />
      <MenuCheckboxSection
        title="Categories"
        onChange={onChangeCheckbox}
        data={categories}
        category={CheckboxCategoryFilter.CATEGORIES}
        selectedValues={filterParams.categories || []}
      />
      <MenuCheckboxSection
        title="Sub-Categories"
        onChange={onChangeCheckbox}
        data={subCategories}
        category={CheckboxCategoryFilter.SUBCATEGORIES}
        selectedValues={filterParams.subCategories || []}
      />
      <MenuCheckboxSection
        title="Gender"
        onChange={onChangeCheckbox}
        data={genders}
        category={CheckboxCategoryFilter.GENDERS}
        selectedValues={filterParams.genders || []}
      />
      <MenuRadioSection
        title="Price Range"
        onChange={onChangeRadio}
        data={[
          { id: 1, name: "1 - 99", array: [1, 99] },
          { id: 2, name: "100 - 499", array: [100, 499] },
          { id: 3, name: "500 - 999", array: [500, 999] },
          { id: 4, name: "1000 - 4999", array: [1000, 4999] },
          { id: 5, name: "5000 - 9999", array: [5000, 9999] },
        ]}
      />
    </>
  );

  return (
    <Layout className="flipkart-style-menu">
      <Layout.Content className="menu-content">
        <Row gutter={[24, 24]}>
          {/* Desktop Filters */}
          {!isMobile && (
            <Col xs={24} md={5} className="filters-col bg-white p-3 rounded-lg shadow-md">
              {FilterContent}
            </Col>
          )}

          {/* MOBILE TOOLBAR */}
          {isMobile && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0 16px",
                marginBottom: "16px",
                gap: "10px",
              }}
            >
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setIsDrawerVisible(true)}
                style={{ flex: 1 }}
              >
                Filters
              </Button>

              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  onClick={() => handleMobileSort("asc")}
                  type={!sortByPrice ? "primary" : "default"}
                >
                  ⬆️ Low
                </Button>
                <Button
                  onClick={() => handleMobileSort("desc")}
                  type={sortByPrice ? "primary" : "default"}
                >
                  ⬇️ High
                </Button>
              </div>

              <Drawer
                title={
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography.Text strong>Filter Products</Typography.Text>
                    <Button type="text" onClick={() => setIsDrawerVisible(false)}>
                      ✕
                    </Button>
                  </div>
                }
                placement="right"
                open={isDrawerVisible}
                onClose={() => setIsDrawerVisible(false)}
                width="70%"
              >
                {FilterContent}
              </Drawer>
            </div>
          )}

          {/* PRODUCTS */}
          <Col xs={24} md={19} className="product-col bg-white p-4 rounded-lg shadow-sm">
            <div className="pagination-top">
              <Pagination
                current={currentPage}
                pageSize={MAX_PAGE_VALUE}
                total={totalElements}
                showSizeChanger={false}
                onChange={changePagination}
              />
            </div>

            {/* MAIN UI RESULT LOGIC */}
            {isLocalLoading || (!skipReduxLoading && isPerfumesLoading) ? (
              <div className="center-wrapper">
                <Spinner />
              </div>
            ) : noProducts ? (
              <div className="center-wrapper">
                <Typography.Text strong style={{ fontSize: 18, color: "#888" }}>
                  No products found in this category
                </Typography.Text>
              </div>
            ) : (
              <div className="product-grid">
                {(localPerfumes.length > 0 ? localPerfumes : perfumes).map((perfume) => (
                  <div key={perfume.id} className="product-card-wrapper">
                    <PerfumeCard perfume={perfume} />
                  </div>
                ))}
              </div>
            )}

            <div className="pagination-bottom">
              <Pagination
                current={currentPage}
                pageSize={MAX_PAGE_VALUE}
                total={totalElements}
                showSizeChanger={false}
                onChange={changePagination}
              />
            </div>
          </Col>
        </Row>
      </Layout.Content>
    </Layout>
  );
};

export default Menu;
